import * as React from "react";
import { createPortal } from "react-dom";
import { Cross2Icon } from "@radix-ui/react-icons";
import { cn } from "@/utils";

interface ModalProps {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
}

export const Modal = ({ open, onClose, children, className }: ModalProps) => {
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    // Handle escape key
    React.useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape" && open) {
                onClose();
            }
        };
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [open, onClose]);

    // Prevent body scroll when open
    React.useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    if (!mounted || !open) return null;

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalContent = (
        <div
            className="tw:fixed tw:inset-0 tw:z-max tw:bg-black/80 tw:animate-in tw:fade-in-0"
            onClick={handleOverlayClick}
        >
            <div
                className={cn(
                    "tw:fixed tw:left-[50%] tw:top-[50%] tw:z-max tw:grid tw:w-[500px] tw:max-w-[90vw] tw:translate-x-[-50%] tw:translate-y-[-50%] tw:border tw:bg-background tw:p-6 tw:shadow-lg tw:animate-in tw:fade-in-0 tw:zoom-in-95 tw:slide-in-from-left-1/2 tw:slide-in-from-top-[48%] tw:rounded-lg tw:max-h-[90vh] tw:overflow-y-auto",
                    className
                )}
            >
                {children}
                <button
                    onClick={onClose}
                    className="tw:absolute tw:right-4 tw:top-4 tw:rounded-sm tw:opacity-70 tw:ring-offset-background tw:transition-opacity tw:hover:opacity-100 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-ring tw:focus:ring-offset-2"
                >
                    <Cross2Icon className="tw:h-4 tw:w-4" />
                    <span className="tw:sr-only">Close</span>
                </button>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

type ModalHeaderProps = React.HTMLAttributes<HTMLDivElement>;

export const ModalHeader = ({ className, ...props }: ModalHeaderProps) => (
    <div
        className={cn("tw:flex tw:flex-col tw:mb-3", className)}
        {...props}
    />
);

type ModalBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const ModalBody = ({ className, ...props }: ModalBodyProps) => (
    <div
        className={cn("tw:flex tw:flex-col tw:gap-4", className)}
        {...props}
    />
);

type ModalFooterProps = React.HTMLAttributes<HTMLDivElement>;

export const ModalFooter = ({ className, ...props }: ModalFooterProps) => (
    <div
        className={cn("tw:flex tw:flex-row tw:justify-between tw:gap-2 tw:mt-4", className)}
        {...props}
    />
);
